/**
 * BatchSelector Component
 * Allows selection of products and batches with quantity allocation for order creation
 *
 * Features:
 * - Manufacturing Unit selection from mfg_manufacturing_unit table (3 units)
 * - Product selection based on manufacturing unit
 * - Batch selection with availability validation
 * - Quantity deduction on order creation
 *
 * Flow:
 * 1. Select Manufacturing Unit → from mfg_manufacturing_unit table
 * 2. Select Product → products available for that manufacturing unit
 * 3. Select Batch → batches with available quantity for that product
 *
 * @author SAUBHAGYA Development Team
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Loader2, Plus, Trash2, Package, AlertCircle, Factory } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';

import {
  getManufacturingUnitsFromDB,
  getProductsByManufacturingUnit,
  getBatchesByUnitAndProduct,
  type ManufacturingUnitFromDB,
  type ProductFromDB,
  type BatchFromDB,
  type BatchSelection,
} from '@/services/commerce/batchService';

interface BatchSelectorProps {
  onSelectionsChange: (selections: BatchSelection[], total: number) => void;
  disabled?: boolean;
}

interface SelectedBatch extends BatchSelection {
  batchCode: string;
  productName: string;
  unitPrice: number;
  availableQuantity: number;
  lineTotal: number;
  manufacturingUnitName?: string;
}

export function BatchSelector({
  onSelectionsChange,
  disabled = false,
}: BatchSelectorProps) {
  // Manufacturing Unit state
  const [manufacturingUnits, setManufacturingUnits] = useState<ManufacturingUnitFromDB[]>([]);
  const [selectedManufacturingUnitId, setSelectedManufacturingUnitId] = useState<number | null>(null);
  const [unitsLoading, setUnitsLoading] = useState(false);

  // Products state
  const [products, setProducts] = useState<ProductFromDB[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [productsLoading, setProductsLoading] = useState(false);

  // Batches state
  const [availableBatches, setAvailableBatches] = useState<BatchFromDB[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);

  // Selected batches for order
  const [selectedBatches, setSelectedBatches] = useState<SelectedBatch[]>([]);

  // Current selection form state
  const [currentBatchId, setCurrentBatchId] = useState<number | null>(null);
  const [currentQuantity, setCurrentQuantity] = useState<string>('');

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Fetch manufacturing units on mount (from mfg_manufacturing_unit table)
  useEffect(() => {
    fetchManufacturingUnits();
  }, []);

  // Fetch products when manufacturing unit changes
  useEffect(() => {
    if (selectedManufacturingUnitId) {
      fetchProducts(selectedManufacturingUnitId);
    } else {
      setProducts([]);
    }
    // Reset product and batch selection when unit changes
    setSelectedProductId(null);
    setAvailableBatches([]);
    setCurrentBatchId(null);
    setCurrentQuantity('');
  }, [selectedManufacturingUnitId]);

  // Fetch batches when product changes
  useEffect(() => {
    if (selectedManufacturingUnitId && selectedProductId) {
      fetchBatches(selectedManufacturingUnitId, selectedProductId);
    } else {
      setAvailableBatches([]);
    }
    // Reset batch selection when product changes
    setCurrentBatchId(null);
    setCurrentQuantity('');
  }, [selectedManufacturingUnitId, selectedProductId]);

  // Notify parent of selection changes
  useEffect(() => {
    const total = selectedBatches.reduce((sum, batch) => sum + batch.lineTotal, 0);
    const selections: BatchSelection[] = selectedBatches.map((batch) => ({
      dungProductId: batch.dungProductId,
      batchId: batch.batchId,
      quantity: batch.quantity,
      unit: batch.unit,
    }));
    onSelectionsChange(selections, total);
  }, [selectedBatches, onSelectionsChange]);

  /**
   * Fetch manufacturing units from mfg_manufacturing_unit table
   */
  const fetchManufacturingUnits = async () => {
    setUnitsLoading(true);
    try {
      const units = await getManufacturingUnitsFromDB();
      console.log('[BatchSelector] Fetched manufacturing units from DB:', units);
      setManufacturingUnits(units);
    } catch (err) {
      console.error('Error fetching manufacturing units:', err);
      setError('Failed to load manufacturing units');
    } finally {
      setUnitsLoading(false);
    }
  };

  /**
   * Fetch products for a specific manufacturing unit
   */
  const fetchProducts = async (unitId: number) => {
    setProductsLoading(true);
    setError(null);
    try {
      const productList = await getProductsByManufacturingUnit(unitId);
      console.log('[BatchSelector] Fetched products for unit', unitId, ':', productList);
      setProducts(productList);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  /**
   * Fetch batches for a specific manufacturing unit and product
   */
  const fetchBatches = async (unitId: number, productId: number) => {
    setBatchesLoading(true);
    try {
      const batchList = await getBatchesByUnitAndProduct(unitId, productId);
      console.log('[BatchSelector] Fetched batches for unit', unitId, 'product', productId, ':', batchList);
      setAvailableBatches(batchList);
    } catch (err) {
      console.error('Error fetching batches:', err);
      setAvailableBatches([]);
    } finally {
      setBatchesLoading(false);
    }
  };

  /**
   * Add a batch to the selection
   */
  const handleAddBatch = useCallback(() => {
    if (!currentBatchId || !currentQuantity || !selectedProductId) return;

    const quantity = parseFloat(currentQuantity);
    if (isNaN(quantity) || quantity <= 0) return;

    const batch = availableBatches.find((b) => b.id === currentBatchId);
    if (!batch) return;

    const selectedProduct = products.find((p) => p.id === selectedProductId);
    const selectedUnit = manufacturingUnits.find((u) => u.id === selectedManufacturingUnitId);

    // Check if already added
    const existingIndex = selectedBatches.findIndex((b) => b.batchId === currentBatchId);

    // Calculate actual available quantity considering existing selections
    const alreadySelected = existingIndex >= 0 ? selectedBatches[existingIndex].quantity : 0;
    const actualAvailable = batch.availableQuantityKg - alreadySelected;

    if (quantity > actualAvailable) {
      setError(`Cannot exceed available quantity (${actualAvailable} kg)`);
      return;
    }

    const unitPrice = batch.price || selectedProduct?.price || 0;

    if (existingIndex >= 0) {
      // Update existing
      const updated = [...selectedBatches];
      updated[existingIndex] = {
        ...updated[existingIndex],
        quantity: updated[existingIndex].quantity + quantity,
        lineTotal: (updated[existingIndex].quantity + quantity) * unitPrice,
      };
      setSelectedBatches(updated);
    } else {
      // Add new
      const newSelection: SelectedBatch = {
        dungProductId: batch.productId || selectedProductId,
        batchId: batch.id,
        quantity,
        unit: 'kg',
        batchCode: batch.batchCode,
        productName: batch.productName || selectedProduct?.productName || 'Unknown Product',
        unitPrice,
        availableQuantity: batch.availableQuantityKg,
        lineTotal: quantity * unitPrice,
        manufacturingUnitName: batch.manufacturingUnitName || selectedUnit?.name,
      };
      setSelectedBatches([...selectedBatches, newSelection]);
    }

    // Reset form
    setCurrentBatchId(null);
    setCurrentQuantity('');
    setError(null);
  }, [currentBatchId, currentQuantity, selectedProductId, availableBatches, selectedBatches, products, manufacturingUnits, selectedManufacturingUnitId]);

  /**
   * Remove a batch from the selection
   */
  const handleRemoveBatch = (batchId: number) => {
    setSelectedBatches(selectedBatches.filter((b) => b.batchId !== batchId));
  };

  /**
   * Update quantity for a selected batch
   */
  const handleQuantityChange = (batchId: number, newQuantity: string) => {
    const quantity = parseFloat(newQuantity);
    if (isNaN(quantity) || quantity <= 0) return;

    const batch = selectedBatches.find((b) => b.batchId === batchId);
    if (!batch) return;

    if (quantity > batch.availableQuantity) {
      setError(`Cannot exceed available quantity (${batch.availableQuantity} ${batch.unit})`);
      return;
    }

    const updated = selectedBatches.map((b) =>
      b.batchId === batchId
        ? { ...b, quantity, lineTotal: quantity * b.unitPrice }
        : b
    );
    setSelectedBatches(updated);
    setError(null);
  };

  const totalAmount = selectedBatches.reduce((sum, b) => sum + b.lineTotal, 0);

  // Get available batches that haven't been fully selected
  const availableForSelection = availableBatches.filter((batch) => {
    const existing = selectedBatches.find((s) => s.batchId === batch.id);
    return !existing || existing.quantity < batch.availableQuantityKg;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Package className="h-5 w-5" />
          Batch Selection
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Manufacturing Unit Selection */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Factory className="h-4 w-4" />
            Step 1: Select Manufacturing Unit *
          </Label>
          <Select
            value={selectedManufacturingUnitId?.toString() ?? ''}
            onValueChange={(val) => setSelectedManufacturingUnitId(parseInt(val))}
            disabled={disabled || unitsLoading}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a manufacturing unit..." />
            </SelectTrigger>
            <SelectContent>
              {unitsLoading ? (
                <div className="flex items-center justify-center p-2">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading manufacturing units...
                </div>
              ) : manufacturingUnits.length === 0 ? (
                <div className="p-2 text-muted-foreground">No manufacturing units available</div>
              ) : (
                manufacturingUnits.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id.toString()}>
                    {unit.name} {unit.location ? `(${unit.location})` : ''}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {manufacturingUnits.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {manufacturingUnits.length} manufacturing unit(s) available from database
            </p>
          )}
        </div>

        {/* Step 2: Product Selection */}
        <div className="space-y-2">
          <Label>Step 2: Select Product *</Label>
          <Select
            value={selectedProductId?.toString() ?? ''}
            onValueChange={(val) => setSelectedProductId(parseInt(val))}
            disabled={disabled || productsLoading || !selectedManufacturingUnitId}
          >
            <SelectTrigger>
              <SelectValue placeholder={
                !selectedManufacturingUnitId
                  ? "First select a manufacturing unit"
                  : productsLoading
                    ? "Loading products..."
                    : products.length === 0
                      ? "No products available for this unit"
                      : "Select a product..."
              } />
            </SelectTrigger>
            <SelectContent>
              {productsLoading ? (
                <div className="flex items-center justify-center p-2">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading products...
                </div>
              ) : products.length === 0 ? (
                <div className="p-2 text-muted-foreground">
                  No products available for this manufacturing unit
                </div>
              ) : (
                products.map((product) => (
                  <SelectItem key={product.id} value={product.id.toString()}>
                    {product.productName} {product.price ? `- ₹${product.price}` : ''}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {selectedManufacturingUnitId && products.length === 0 && !productsLoading && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                No products are available in this manufacturing unit. Please select a different manufacturing unit.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {/* Step 3: Batch Selection Form */}
        {selectedProductId && selectedManufacturingUnitId && (
          <div className="space-y-2">
            <Label>Step 3: Select Batch and Quantity *</Label>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Select
                  value={currentBatchId?.toString() ?? ''}
                  onValueChange={(val) => setCurrentBatchId(parseInt(val))}
                  disabled={disabled || batchesLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      batchesLoading ? "Loading batches..." : "Select batch..."
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {batchesLoading ? (
                      <div className="flex items-center justify-center p-2">
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Loading batches...
                      </div>
                    ) : availableForSelection.length === 0 ? (
                      <div className="p-2 text-muted-foreground">No batches available</div>
                    ) : (
                      availableForSelection.map((batch) => (
                        <SelectItem key={batch.id} value={batch.id.toString()}>
                          {batch.batchCode} - {batch.availableQuantityKg} kg @ ₹{batch.price || 0}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Input
                  type="number"
                  placeholder="Enter quantity (kg)"
                  value={currentQuantity}
                  onChange={(e) => setCurrentQuantity(e.target.value)}
                  disabled={disabled || !currentBatchId}
                  min="1"
                  step="1"
                />
              </div>

              <div>
                <Button
                  type="button"
                  onClick={handleAddBatch}
                  disabled={disabled || !currentBatchId || !currentQuantity}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Batch
                </Button>
              </div>
            </div>
            {availableForSelection.length === 0 && !batchesLoading && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  No batches are available for this product. Please select a different product.
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}

        {/* Selected Batches Table */}
        {selectedBatches.length > 0 && (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Batch Code</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Quantity</TableHead>
                  <TableHead className="text-right">Unit Price</TableHead>
                  <TableHead className="text-right">Line Total</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {selectedBatches.map((batch) => (
                  <TableRow key={batch.batchId}>
                    <TableCell className="font-medium">{batch.productName}</TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge variant="outline">{batch.batchCode}</Badge>
                        {batch.manufacturingUnitName && (
                          <span className="text-xs text-muted-foreground">{batch.manufacturingUnitName}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {batch.availableQuantity} {batch.unit}
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        value={batch.quantity}
                        onChange={(e) => handleQuantityChange(batch.batchId, e.target.value)}
                        className="w-24 text-right"
                        disabled={disabled}
                        min="1"
                        max={batch.availableQuantity}
                        step="1"
                      />
                    </TableCell>
                    <TableCell className="text-right">₹{batch.unitPrice.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">
                      ₹{batch.lineTotal.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveBatch(batch.batchId)}
                        disabled={disabled}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Order Summary */}
        {selectedBatches.length > 0 && (
          <div className="flex justify-end">
            <div className="w-64 space-y-2 text-right">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal:</span>
                <span className="font-medium">₹{totalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>CGST (9%):</span>
                <span>₹{(totalAmount * 0.09).toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>SGST (9%):</span>
                <span>₹{(totalAmount * 0.09).toFixed(2)}</span>
              </div>
              <div className="border-t pt-2 flex justify-between text-lg font-semibold">
                <span>Total:</span>
                <span>₹{(totalAmount * 1.18).toFixed(2)}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default BatchSelector;
